import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Minus, Plus, X, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CartPage = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState(() => {
    const saved = localStorage.getItem('cart');
    return saved ? JSON.parse(saved) : [];
  });
  const [loading, setLoading] = useState(false);

  const updateQuantity = (id, newQuantity) => {
    if (newQuantity < 1) return;
    const updated = cartItems.map(item =>
      item.id === id ? { ...item, quantity: newQuantity } : item
    );
    setCartItems(updated);
    localStorage.setItem('cart', JSON.stringify(updated));
  };

  const removeItem = (id) => {
    const updated = cartItems.filter(item => item.id !== id);
    setCartItems(updated);
    localStorage.setItem('cart', JSON.stringify(updated));
    toast.success('Item removed from cart');
  };

  const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const tax = subtotal * 0.13; // 13% tax
  const total = subtotal + tax;

  const handleCheckout = async () => {
    setLoading(true);
    try {
      // Create Stripe session with $6.99 flat-rate shipping
      const lineItems = [
        ...cartItems.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          discount: item.discount || 0,
          tier: item.tier || 'Retail'
        })),
        // Add flat-rate shipping
        {
          id: 'shipping',
          name: 'Shipping',
          price: 6.99,
          quantity: 1,
          discount: 0,
          tier: 'Shipping'
        }
      ];

      const response = await axios.post(`${API}/checkout/create-session`, {
        items: lineItems
      });

      // Redirect directly to Stripe
      if (response.data && response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unable to process checkout';
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <button onClick={() => { window.scrollTo(0, 0); navigate('/'); }} className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_elite-lighting/artifacts/27f7yllt_WhatsApp%20Image%202025-10-18%20at%2008.58.35.jpeg" 
                alt="ONYX Home Solutions" 
                className="h-12 lg:h-16 w-auto object-contain"
                style={{ background: 'transparent' }}
              />
            </button>
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => { window.scrollTo(0, 0); navigate('/'); }} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                Home
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/products'); }} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                Products
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/bulk-pricing'); }} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                Bulk
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/guide'); }} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                Guides
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/become-partner'); }} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                Become a Partner
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/admin/login'); }} className="text-sm font-medium transition-colors" style={{ color: '#8B6F47' }}>
                Login
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/cart'); }} className="relative p-2 rounded-full transition-colors" style={{ color: '#8B6F47', background: '#fef3e4' }}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                {cartItems.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: 'linear-gradient(135deg, #D4A574 0%, #8B6F47 100%)' }}>
                    {cartItems.length}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Cart Content */}
      <div className="pt-32 pb-20 px-4 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl lg:text-5xl font-bold mb-8" style={{ color: '#1d1d1f' }}>
            Shopping Cart
          </h1>

          {cartItems.length === 0 ? (
            <div className="bg-white rounded-3xl p-16 text-center shadow-xl">
              <ShoppingBag className="w-24 h-24 mx-auto mb-6 text-gray-300" />
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1d1d1f' }}>Your cart is empty</h2>
              <p className="text-gray-600 mb-8">Add some products to get started!</p>
              <Button 
                onClick={() => { window.scrollTo(0, 0); navigate('/products'); }}
                className="text-white px-8 py-4 rounded-full"
                style={{ background: 'linear-gradient(135deg, #D4A574 0%, #8B6F47 100%)' }}
              >
                Continue Shopping
              </Button>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4">
                {cartItems.map((item) => (
                  <div key={item.id} className="bg-white rounded-2xl p-6 shadow-lg flex gap-6">
                    <img 
                      src={item.image}
                      alt={item.name}
                      className="w-32 h-32 object-cover rounded-xl"
                    />
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-bold" style={{ color: '#1d1d1f' }}>{item.name}</h3>
                          <p className="text-2xl font-bold mt-2" style={{ color: '#8B6F47' }}>${item.price.toFixed(2)}</p>
                        </div>
                        <button 
                          onClick={() => removeItem(item.id)}
                          className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                        >
                          <X className="w-5 h-5 text-gray-400" />
                        </button>
                      </div>
                      <div className="flex items-center gap-3">
                        <button 
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-10 h-10 rounded-full border-2 flex items-center justify-center hover:bg-gray-50 transition-colors"
                          style={{ borderColor: '#D4A574' }}
                        >
                          <Minus className="w-4 h-4" style={{ color: '#8B6F47' }} />
                        </button>
                        <span className="text-xl font-semibold w-12 text-center">{item.quantity}</span>
                        <button 
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-10 h-10 rounded-full border-2 flex items-center justify-center hover:bg-gray-50 transition-colors"
                          style={{ borderColor: '#D4A574' }}
                        >
                          <Plus className="w-4 h-4" style={{ color: '#8B6F47' }} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-2xl p-8 shadow-xl sticky top-32">
                  <h2 className="text-2xl font-bold mb-6" style={{ color: '#1d1d1f' }}>Order Summary</h2>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="text-lg font-semibold">${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Shipping</span>
                      <span className="text-lg font-semibold">$6.99</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Tax (13%)</span>
                      <span className="text-lg font-semibold">${tax.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-gray-200 mb-6">
                    <div className="flex items-center justify-between">
                      <span className="text-xl font-bold">Total</span>
                      <span className="text-3xl font-bold" style={{ color: '#8B6F47' }}>${(total + 6.99).toFixed(2)}</span>
                    </div>
                  </div>

                  <Button 
                    onClick={handleCheckout}
                    disabled={loading}
                    className="w-full text-white py-4 rounded-full text-lg font-medium mb-4"
                    style={{ background: 'linear-gradient(135deg, #D4A574 0%, #8B6F47 100%)' }}
                  >
                    {loading ? 'Processing...' : 'Proceed to Checkout'}
                  </Button>

                  <button 
                    onClick={() => { window.scrollTo(0, 0); navigate('/products'); }}
                    className="w-full text-center text-sm font-medium py-3 rounded-full hover:bg-gray-50 transition-colors"
                    style={{ color: '#8B6F47' }}
                  >
                    Continue Shopping
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="py-12 px-4 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <p className="text-gray-400 text-sm">Premium LED lighting and HVAC solutions for modern homes.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Products</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => { window.scrollTo(0, 0); navigate('/products'); }} className="text-gray-400 hover:text-white">ONYX Lighting</button></li>
                <li><button onClick={() => { window.scrollTo(0, 0); navigate('/products'); }} className="text-gray-400 hover:text-white">ONYX HVAC</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => { window.scrollTo(0, 0); navigate('/'); }} className="text-gray-400 hover:text-white">About Us</button></li>
                <li><button onClick={() => { window.scrollTo(0, 0); navigate('/become-partner'); }} className="text-gray-400 hover:text-white">Become a Partner</button></li>
                <li><button onClick={() => { window.scrollTo(0, 0); navigate('/guide'); }} className="text-gray-400 hover:text-white">Guides</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="mailto:info@onyx-homesolutions.com" className="text-gray-400 hover:text-white">info@onyx-homesolutions.com</a></li>
                <li><a href="tel:519-996-2866" className="text-gray-400 hover:text-white">519-996-2866</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-gray-800 text-center text-sm text-gray-400">
            <p>© 2025 ONYX Home Solutions. All rights reserved. | Powered by Syntrexe</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CartPage;
